import { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getDefaultName, getDefaultMembership } from '../utils/selectionUtils'
import {
    setContactSearchTermAction,
    resetContactSearchAction
} from '../store/actions'
import {
    selectContactSearchResults,
    selectContactSearchTotalCount,
    selectContactSearchFetchStatus
} from '../store/selectors'

/**
 * Custom hook for managing Contact Search and Select modal state
 * @param {boolean} visible - Whether the modal is visible
 * @returns {Object} - State and handlers for the modal
 */
const useContactSearch = (visible) => {
    const dispatch = useDispatch()

    // Search results from Redux
    const searchResults = useSelector(selectContactSearchResults)
    const totalCount = useSelector(selectContactSearchTotalCount)
    const fetchStatus = useSelector(selectContactSearchFetchStatus)

    // Derive loading state from fetch status
    const isLoading = fetchStatus?.status === 'pending'

    // Local search state
    const [hasSearched, setHasSearched] = useState(false)

    // Selection state
    const [selectedContact, setSelectedContact] = useState(null)
    const [selectedName, setSelectedName] = useState(null)
    const [selectedMembership, setSelectedMembership] = useState(null)

    // Track previous results length to detect new results
    const prevResultsLengthRef = useRef(0)

    // Reset all state when modal closes
    useEffect(() => {
        if (!visible) {
            setSelectedContact(null)
            setSelectedName(null)
            setSelectedMembership(null)
            setHasSearched(false)
            prevResultsLengthRef.current = 0

            // Reset Redux state
            dispatch(resetContactSearchAction())
        }
    }, [visible, dispatch])

    // Auto-select first contact when new results arrive or when selected contact is not in results
    useEffect(() => {
        if (searchResults.length > 0) {
            // If there's no selected contact, or the selected contact is not in the current results
            // (which happens after filtering/sorting), select the first row
            const isSelectedInResults = selectedContact &&
                searchResults.some(r => r.id === selectedContact.id)

            if (!selectedContact || !isSelectedInResults) {
                setSelectedContact(searchResults[0])
            }
        }
        prevResultsLengthRef.current = searchResults.length
    }, [searchResults, selectedContact])

    // Auto-select default name and membership when contact is selected
    useEffect(() => {
        if (selectedContact) {
            // Auto-select default name (Main Writing > Pseudonym > Correspondence if only one)
            const defaultName = getDefaultName(selectedContact.names)
            setSelectedName(defaultName)

            // Auto-select default membership (first non-Successor)
            const defaultMembership = getDefaultMembership(selectedContact.memberships)
            setSelectedMembership(defaultMembership)
        } else {
            setSelectedName(null)
            setSelectedMembership(null)
        }
    }, [selectedContact])

    // Handle search - dispatch search term to Redux, ContactSearchApi will handle the API call
    const handleSearch = useCallback((term) => {
        setHasSearched(true)
        prevResultsLengthRef.current = 0
        setSelectedContact(null)

        // Dispatch searchTerm to Redux - ContactSearchApi will detect this and make API call
        dispatch(setContactSearchTermAction(term ?? ''))
    }, [dispatch])

    // Handle contact selection - prevent deselection to keep at least one row selected
    const handleContactSelect = useCallback((contact) => {
        if (contact) {
            setSelectedContact(contact)
        }
    }, [])

    // Handle name selection
    const handleNameSelect = useCallback((name) => {
        setSelectedName(name)
    }, [])

    // Handle membership selection
    const handleMembershipSelect = useCallback((membership) => {
        setSelectedMembership(membership)
    }, [])

    // Clear search and results
    const handleClearSearch = useCallback(() => {
        setSelectedContact(null)
        setSelectedName(null)
        setSelectedMembership(null)
        setHasSearched(false)
        prevResultsLengthRef.current = 0
        dispatch(resetContactSearchAction())
    }, [dispatch])

    return {
        // Search state
        searchResults,
        totalCount,
        isLoading,
        hasSearched,

        // Selection state
        selectedContact,
        selectedName,
        selectedMembership,

        // Handlers
        handleSearch,
        handleContactSelect,
        handleNameSelect,
        handleMembershipSelect,
        handleClearSearch
    }
}

export default useContactSearch
